import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Play, Instagram, Facebook, Linkedin, FileText, Loader2, CheckCircle, Clock, RefreshCw, Wifi, ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChecklistItem = ({ number, title, description, link, linkText, done, children }) => (
  <div className={`p-4 rounded-lg border ${done ? 'bg-green-50 border-green-200' : 'bg-white border-stone-200'}`}>
    <div className="flex items-start gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        done ? 'bg-green-500 text-white' : 'bg-stone-200 text-stone-600'
      }`}>
        {done ? <CheckCircle size={16} /> : number}
      </div>
      <div className="flex-1">
        <h3 className={`font-medium ${done ? 'text-green-800' : 'text-stone-900'}`}>{title}</h3>
        <p className="text-sm text-stone-600 mt-1">{description}</p>
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700 mt-1 inline-block">
            {linkText}
          </a>
        )}
        {children}
      </div>
    </div>
  </div>
);

const FAQItem = ({ question, answer }) => (
  <div className="border-b border-stone-100 pb-3 last:border-0">
    <h3 className="font-medium text-stone-900">{question}</h3>
    <p className="text-sm text-stone-600 mt-1">{answer}</p>
  </div>
);

const SetupGuidePage = () => {
  const [metaApiStatus, setMetaApiStatus] = useState(null);
  const [rionaLogs, setRionaLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Diese Seite ist ein internes Einrichtungs-Werkzeug für den Inhaber (API-/Token-Setup,
  // Aktivitäts-Logs). Endkunden sollen sie NICHT sehen — für sie ist „Kanäle verbinden"
  // (/kanale) der richtige Ort.
  // FAIL-CLOSED wie in frontend/src/App.js: null = Antwort steht noch aus. Vorher
  // `useState(true)` + `is_master !== false` — dadurch blitzte die interne Inhaber-
  // Ansicht (API-Status, Riona-Logs) beim Kunden auf, bevor /account/me antwortete.
  // Solange der Wert null ist, wird unten NICHTS von beidem gerendert, sondern der
  // Ladezustand — so sieht weder der Kunde Internas noch der Inhaber ein Aufblitzen
  // der Kundenansicht.
  const [isMaster, setIsMaster] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/account/me`)
      .then((res) => { if (active) setIsMaster(res.data?.is_master === true); })
      .catch(() => { if (active) setIsMaster(false); });   // fail-closed
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, logsRes] = await Promise.all([
          axios.get(`${API}/riona/meta-api/status`),
          axios.get(`${API}/riona/logs?limit=20`)
        ]);
        setMetaApiStatus(statusRes.data);
        setRionaLogs(logsRes.data?.logs || []);
      } catch (error) {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const refreshLogs = async () => {
    try {
      const logsRes = await axios.get(`${API}/riona/logs?limit=20`);
      setRionaLogs(logsRes.data?.logs || []);
      toast.success('Logs aktualisiert');
    } catch {
      toast.error('Fehler beim Laden der Logs');
    }
  };

  // Laden ODER Rolle noch unbekannt → nichts entscheiden, nichts zeigen.
  if (loading || isMaster === null) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
      </div>
    );
  }

  // Endkunden-Ansicht: kein internes Setup, sondern direkt zur Kanal-Verbindung führen.
  if (isMaster !== true) {
    return (
      <div data-testid="setup-guide-customer" className="max-w-xl mx-auto py-12 text-center space-y-4">
        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mx-auto">
          <Wifi size={24} className="text-fox-fire" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-stone-900">Kanäle verbinden</h1>
        <p className="text-stone-500">
          Verbinde deine Social-Media-Kanäle mit einem Klick — danach veröffentlicht Förderfuchs
          deine freigegebenen Beiträge automatisch für dich.
        </p>
        <Link
          to="/kanale"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-fox-fire text-white font-medium hover:bg-orange-600"
        >
          Zu „Kanäle verbinden" <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="setup-guide-page" className="space-y-8 animate-fade-in">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900">Anleitung & Erste Schritte</h1>
        <p className="text-stone-500 mt-1">So richtest du deine Kanäle ein und veröffentlichst deinen ersten Beitrag.</p>
      </div>

      {/* Quick Start */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Play size={24} className="text-blue-600" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-blue-900 mb-2">
              🎯 So erstellst du deinen ersten Beitrag (Schritt für Schritt)
            </h2>
            <div className="space-y-3 text-blue-800">
              {[
                'Gehe zu „Monatsplan" in der Seitenleiste — dein Plan wird automatisch erstellt',
                'Steht dort noch kein Plan, erzeugst du ihn mit einem Klick; sobald er da ist, verschwindet die Schaltfläche wieder',
                'Klick auf das Datum eines Beitrags — du siehst die Vorschau und kannst den Text jederzeit anpassen',
                'Verbinde deinen ersten Kanal (z.B. Facebook oder Instagram) unter „Kanäle"',
                'Kehre zum Monatsplan zurück und klicke „Veröffentlichen" — fertig!',
                'Tipp: Kanäle verbinden dauert etwa 5 Minuten — danach läuft alles automatisch.',
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="bg-blue-200 text-blue-800 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <p>{step}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>💡 Tipp:</strong> Probiere alle Kanäle aus! Der Content wird automatisch für jeden Kanal angepasst
                (z.B. mehr Hashtags für Instagram, professioneller Ton für LinkedIn).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Instagram Checkliste */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
            <Instagram size={20} className="text-white" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Checkliste für echtes Instagram-Posting</h2>
            <p className="text-sm text-stone-500">Meta Graph API Setup (offiziell & sicher)</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className={`p-4 rounded-lg ${metaApiStatus?.status?.configured ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center gap-2">
              {metaApiStatus?.status?.configured ? (
                <CheckCircle className="text-green-600" size={20} />
              ) : (
                <Clock className="text-amber-600" size={20} />
              )}
              <span className={`font-medium ${metaApiStatus?.status?.configured ? 'text-green-800' : 'text-amber-800'}`}>
                Status: {metaApiStatus?.status?.message || 'Wird geladen...'}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <ChecklistItem number={1} title="Instagram Business/Creator Account" description="Wandle deinen privaten Instagram-Account in einen Business oder Creator Account um." link="https://help.instagram.com/502981923235522" linkText="Instagram Hilfe →" done={false} />
            <ChecklistItem number={2} title="Facebook-Seite erstellen" description="Erstelle eine Facebook-Seite für dein Unternehmen (Förderfuchs) falls noch nicht vorhanden." link="https://www.facebook.com/pages/create" linkText="Seite erstellen →" done={false} />
            <ChecklistItem number={3} title="Instagram mit Facebook verbinden" description="Verbinde deinen Instagram Business Account mit deiner Facebook-Seite." link="https://help.instagram.com/399237934150902" linkText="Verbindungsanleitung →" done={false} />
            <ChecklistItem number={4} title="Meta Developer App erstellen" description="Erstelle eine App im Meta Developer Portal und aktiviere die Instagram Graph API." link="https://developers.facebook.com/apps/" linkText="Developer Portal →" done={false} />
            <ChecklistItem number={5} title="Zugangs-Token erstellen" description="Erstelle deinen Zugangs-Schlüssel für die App. Folge dazu der Token-Anleitung von Meta (Link rechts) — ca. 5 Minuten, keine Programmierkenntnisse nötig." link="https://developers.facebook.com/docs/instagram-api/getting-started" linkText="Token-Anleitung →" done={metaApiStatus?.status?.has_access_token} />
            <ChecklistItem number={6} title="Instagram Business ID finden" description="Hole deine Instagram Business Account ID über die Graph API oder das Developer Portal." link="https://developers.facebook.com/docs/instagram-api/reference/ig-user" linkText="API-Referenz →" done={metaApiStatus?.status?.has_instagram_id} />
            <ChecklistItem number={7} title="Schlüssel in der App hinterlegen" description="Gib Token und Business-ID in den Einstellungen ein — sie werden verschlüsselt gespeichert:" done={metaApiStatus?.status?.configured}>
              <div className="mt-2 p-3 bg-stone-100 rounded font-mono text-xs">
                <p>META_ACCESS_TOKEN=dein_access_token</p>
                <p>INSTAGRAM_BUSINESS_ID=deine_business_id</p>
              </div>
            </ChecklistItem>
          </div>

          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>✅ Sobald du Token und Business-ID hast:</strong><br />
              Schreibe mir einfach „Hier sind meine Meta API Zugangsdaten: TOKEN=... ID=..."
              und ich aktiviere das echte Instagram-Posting für dich.
            </p>
          </div>
        </div>
      </div>

      {/* Facebook Checkliste */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
            <Facebook size={20} className="text-white" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Checkliste für echtes Facebook-Posting</h2>
            <p className="text-sm text-stone-500">Facebook Graph API Setup</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2">
              <Clock className="text-blue-600" size={20} />
              <span className="font-medium text-blue-800">Simulation aktiv — für echtes Posten jetzt Kanal verbinden</span>
            </div>
          </div>

          <div className="space-y-3">
            <ChecklistItem number={1} title="Facebook-Seite für Förderfuchs" description="Du benötigst eine Facebook-Unternehmensseite, um über die API zu posten." link="https://www.facebook.com/pages/create" linkText="Seite erstellen →" done={false} />
            <ChecklistItem number={2} title="Meta Developer App erstellen" description="Erstelle eine App im Meta Developer Portal und aktiviere die Pages API." link="https://developers.facebook.com/apps/" linkText="Developer Portal →" done={false} />
            <ChecklistItem number={3} title="Page Access Token generieren" description="Generiere einen langlebigen Page Access Token mit den Berechtigungen: pages_manage_posts, pages_read_engagement" link="https://developers.facebook.com/docs/pages/access-tokens" linkText="Token-Anleitung →" done={false} />
            <ChecklistItem number={4} title="Page ID ermitteln" description="Hole die Page ID aus den Facebook-Seiteneinstellungen oder über die API." link="https://developers.facebook.com/docs/pages/publishing" linkText="API-Referenz →" done={false} />
            <ChecklistItem number={5} title="Tokens als Umgebungsvariablen setzen" description="Teile mir die Tokens mit:" done={false}>
              <div className="mt-2 p-3 bg-stone-100 rounded font-mono text-xs">
                <p>FACEBOOK_PAGE_ID=deine_page_id</p>
                <p>FACEBOOK_ACCESS_TOKEN=dein_page_access_token</p>
              </div>
            </ChecklistItem>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>💡 Tipp:</strong> Die Facebook Page API und Instagram Graph API können denselben Access Token verwenden,
              wenn deine Facebook-Seite mit deinem Instagram Business Account verbunden ist.
            </p>
          </div>
        </div>
      </div>

      {/* LinkedIn Checkliste */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-700 rounded-xl flex items-center justify-center">
            <Linkedin size={20} className="text-white" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Checkliste für echtes LinkedIn-Posting</h2>
            <p className="text-sm text-stone-500">LinkedIn API Setup (OAuth 2.0)</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2">
              <Clock className="text-blue-600" size={20} />
              <span className="font-medium text-blue-800">Simulation aktiv — für echtes Posten jetzt Kanal verbinden</span>
            </div>
          </div>

          <div className="space-y-3">
            <ChecklistItem number={1} title="LinkedIn Company Page erstellen" description="Du benötigst eine LinkedIn-Unternehmensseite für Förderfuchs." link="https://www.linkedin.com/company/setup/new/" linkText="Company Page erstellen →" done={false} />
            <ChecklistItem number={2} title="LinkedIn Developer App erstellen" description="Erstelle eine App im LinkedIn Developer Portal." link="https://www.linkedin.com/developers/apps" linkText="Developer Portal →" done={false} />
            <ChecklistItem number={3} title="Marketing Developer Platform beantragen" description="Für das Posten auf Company Pages benötigst du Zugang zur Marketing Developer Platform." link="https://www.linkedin.com/developers/apps" linkText="Zugang beantragen →" done={false} />
            <ChecklistItem number={4} title="OAuth 2.0 Access Token generieren" description="Generiere einen Access Token mit den Scopes: w_member_social, w_organization_social" link="https://learn.microsoft.com/en-us/linkedin/shared/authentication/authorization-code-flow" linkText="OAuth-Anleitung →" done={false} />
            <ChecklistItem number={5} title="Person/Organization URN ermitteln" description="Hole deine LinkedIn Person ID (für persönliche Posts) oder Organization ID (für Company Posts)." link="https://learn.microsoft.com/en-us/linkedin/marketing/integrations/community-management/shares/posts-api" linkText="API-Referenz →" done={false} />
            <ChecklistItem number={6} title="Tokens als Umgebungsvariablen setzen" description="Teile mir die Tokens mit:" done={false}>
              <div className="mt-2 p-3 bg-stone-100 rounded font-mono text-xs">
                <p>LINKEDIN_ACCESS_TOKEN=dein_access_token</p>
                <p>LINKEDIN_PERSON_ID=urn:li:person:deine_id</p>
              </div>
            </ChecklistItem>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Hinweis:</strong> LinkedIn API-Zugang für Company Pages ist restriktiver als für persönliche Profile.
              Die Genehmigung für die Marketing Developer Platform kann einige Tage dauern.
            </p>
          </div>
        </div>
      </div>

      {/* Aktions-Logs */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <FileText size={20} className="text-purple-600" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold">Riona Aktions-Logs</h2>
              <p className="text-sm text-stone-500">Hier siehst du alle simulierten Aktionen</p>
            </div>
          </div>
          <button
            onClick={refreshLogs}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
          >
            <RefreshCw size={16} /> Aktualisieren
          </button>
        </div>

        {rionaLogs.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {rionaLogs.map((log, index) => (
              <div
                key={log.id || index}
                className={`p-3 rounded-lg text-sm border ${
                  ['success', 'simulated', 'logged_only'].includes(log.status)
                    ? 'bg-green-50 border-green-200'
                    : log.status === 'rate_limited'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-stone-50 border-stone-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      log.mode === 'live' ? 'bg-orange-200 text-orange-800' : 'bg-blue-200 text-blue-800'
                    }`}>
                      {log.mode === 'live' ? '📝 LOG' : '🟢 SIM'}
                    </span>
                    <span className="font-medium text-stone-900 capitalize">{log.action_type?.replace('_', ' ')}</span>
                    <span className={`text-xs ${
                      ['success', 'simulated', 'logged_only'].includes(log.status) ? 'text-green-600' :
                      log.status === 'rate_limited' ? 'text-amber-600' : 'text-stone-500'
                    }`}>
                      ({log.status})
                    </span>
                  </div>
                  <span className="text-xs text-stone-400">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString('de-DE') : ''}
                  </span>
                </div>
                {log.target && <p className="text-xs text-stone-500 mt-1">Ziel: {log.target}</p>}
                {log.details?.caption_preview && (
                  <p className="text-xs text-stone-600 mt-1 italic">"{log.details.caption_preview}..."</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-stone-500">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p>Noch keine Aktionen geloggt.</p>
            <p className="text-sm">Gehe zum Multi-Channel Manager und generiere Content!</p>
          </div>
        )}
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-heading text-xl font-bold mb-4">❓ Häufige Fragen</h2>
        <div className="space-y-4">
          <FAQItem question="Warum wird nichts wirklich gepostet?" answer="Der Simulationsmodus ist absichtlich so eingestellt. Browser-Automation wird von Instagram blockiert. Für echtes Posting benötigst du die Meta Graph API (siehe Checkliste oben)." />
          <FAQItem question="Was passiert mit meinen generierten Posts?" answer="Alle Posts werden in der Datenbank gespeichert und im Kalender angezeigt. Du kannst sie jederzeit bearbeiten oder für später planen." />
          <FAQItem question="Kann ich die Limits für Likes/Kommentare ändern?" answer="Die Limits (30 Likes/Std, 100/Tag, 10 Kommentare/Tag) sind Sicherheitsgrenzen für die spätere Meta API. Sie schützen deinen Account vor Sperren." />
          <FAQItem question="Wie lange dauert das Meta API Setup?" answer="Ca. 15-30 Minuten, wenn du bereits einen Instagram Business Account hast. Sonst ca. 1 Stunde für die komplette Einrichtung." />
        </div>
      </div>
    </div>
  );
};

export default SetupGuidePage;
